import React, {useState} from 'react';

import {AddToCartEvent, BuyablesAddedToCart, CartBuyable} from '@udemy/cart';
import {onEnterAndSpace} from '@udemy/design-system-utils';
import {Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {udSentry} from '@udemy/sentry';
import {ShoppingClient, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import shoppingItemStyles from '../shopping-item/shopping-item.module.less';
import {ActionSupportedLayoutsProps} from '../shopping-item/types';

interface UpsellAddToCartActionProps {
    item: UdemyShoppingItem;
    layout: ActionSupportedLayoutsProps;
}

/**
 * "Add to Cart" action for upsell rows (suggested course + voucher).
 * Unlike the standard Actions component, this does not assume the item is already
 * in a shopping list — it calls ShoppingClient.addToList directly.
 */
export const UpsellAddToCartAction: React.FC<UpsellAddToCartActionProps> = ({item, layout}) => {
    const {gettext} = useI18n();
    const {buyable} = item;
    const [failed, setFailed] = useState(false);

    async function handleAddToCart() {
        try {
            await ShoppingClient.addToList('cart', [buyable]);

            // ShoppingClient.addToList does not update the local ShoppingList
            // (unlike moveToList which calls lists[dest].add()). Push the item into
            // addAttempts directly so MobX observers react immediately.
            ShoppingClient.lists.cart.add(item);

            const trackingBuyable = {
                type: buyable.buyable_object_type,
                id: buyable.id,
                trackingId: buyable.frontendTrackingId,
            } as CartBuyable;

            Tracker.publishEvent(
                new AddToCartEvent({
                    buyables: [trackingBuyable],
                    uiRegion: 'voucher_upsell_bundle',
                }),
            );

            Tracker.publishEvent(
                new BuyablesAddedToCart({
                    buyables: [trackingBuyable],
                    uiRegion: 'voucher_upsell_bundle',
                }),
            );
        } catch (e) {
            udSentry.captureException(e, {
                upsell_buyable_type: buyable.buyable_object_type,
                upsell_buyable_id: String(buyable.id),
            });
            setFailed(true);
        }
    }

    return (
        <div className={shoppingItemStyles[`${layout}-actions`]}>
            <Button
                typography="ud-text-sm"
                udStyle="ghost"
                size="xsmall"
                // if the move to cart fails, the button will be disabled
                disabled={failed}
                onClick={handleAddToCart}
                onKeyPress={onEnterAndSpace(handleAddToCart)}
            >
                {gettext('Move to Cart')}
            </Button>
        </div>
    );
};
